/**
 * HVN Zones Plugin for LightweightCharts v5
 *
 * Renders high volume node bands: merged zones around the volume concentration
 * nodes of the last up-to-5 settled RTH sessions, as full-width translucent
 * horizontal bands with a right-edge label. Settle-static data: the backend
 * rebuilds the set at startup and at the session-date roll; there is no
 * touch-invalidation (zones persist for their age window by design).
 *
 * Data: one blob over SSE channel 'hvn-zones' (snapshot on connect, update on
 * rebuilds) with a REST fallback on enable. Labels are descriptive only.
 */

import { CONFIG } from '../config';
import { authFetch } from '../auth/authFetch';
import { unifiedSSE } from '../hooks/useUnifiedSSE';

const DEBUG = false;
const dlog = (...args) => { if (DEBUG) console.log(...args); };

const FILL = 'rgba(56, 189, 248, 0.10)';
const EDGE = 'rgba(56, 189, 248, 0.25)';
const LABEL = 'rgba(125, 211, 252, 0.85)';

class HVNZonesRenderer {
  constructor(series) {
    this.series = series;
    this.bands = [];       // [{lo, hi, newest}]
    this.enabled = false;
  }

  draw(target) {
    if (!this.enabled || !this.bands.length || !this.series) return;

    target.useBitmapCoordinateSpace(scope => {
      const ctx = scope.context;
      const hR = scope.horizontalPixelRatio;
      const vR = scope.verticalPixelRatio;
      const width = scope.bitmapSize.width;
      const height = scope.bitmapSize.height;

      ctx.save();
      ctx.font = `${Math.round(10 * vR)}px sans-serif`;

      for (const band of this.bands) {
        const yHi = this.series.priceToCoordinate(band.hi);
        const yLo = this.series.priceToCoordinate(band.lo);
        if (yHi === null || yHi === undefined || yLo === null || yLo === undefined) continue;
        let top = Math.min(yHi, yLo) * vR;
        let bot = Math.max(yHi, yLo) * vR;
        if (bot < 0 || top > height) continue;   // fully off-screen
        top = Math.max(top, 0);
        bot = Math.min(bot, height);

        ctx.fillStyle = FILL;
        ctx.fillRect(0, top, width, bot - top);
        ctx.strokeStyle = EDGE;
        ctx.lineWidth = 1 * vR;
        ctx.beginPath();
        ctx.moveTo(0, top);
        ctx.lineTo(width, top);
        ctx.moveTo(0, bot);
        ctx.lineTo(width, bot);
        ctx.stroke();

        const label = `HVN ${String(band.newest || '').slice(5)}`;
        const textW = ctx.measureText(label).width;
        const pad = 3 * hR;
        ctx.fillStyle = 'rgba(17, 24, 39, 0.85)';
        ctx.fillRect(width - textW - 2 * pad, top, textW + 2 * pad, 11 * vR);
        ctx.fillStyle = LABEL;
        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';
        ctx.fillText(label, width - textW - pad, top + 1 * vR);
      }

      ctx.restore();
    });
  }
}

class HVNZonesView {
  constructor(series) {
    this._renderer = new HVNZonesRenderer(series);
  }

  renderer() {
    return this._renderer;
  }
}

class HVNZonesPrimitive {
  constructor(series) {
    this._view = new HVNZonesView(series);
    this._requestUpdate = null;
    this._rafPending = false;
  }

  attached({ requestUpdate }) {
    this._requestUpdate = requestUpdate;
  }

  detached() {
    this._requestUpdate = null;
    this._rafPending = false;
  }

  updateAllViews() {
    return this._view;
  }

  paneViews() {
    return [this._view];
  }

  scheduleUpdate() {
    if (this._requestUpdate && !this._rafPending) {
      this._rafPending = true;
      requestAnimationFrame(() => {
        this._rafPending = false;
        if (this._requestUpdate) this._requestUpdate();
      });
    }
  }

  setBands(bands, enabled) {
    this._view._renderer.bands = bands;
    this._view._renderer.enabled = enabled;
    this.scheduleUpdate();
  }
}

export class HVNZonesPlugin {
  constructor(chart, series) {
    this.chart = chart;
    this.series = series;
    this.primitive = null;
    this.enabled = false;
    this.data = null;      // last blob {as_of_ts, bands: [{lo, hi, newest}]}
    this._sseUnsubs = [];
  }

  _redraw() {
    if (this.primitive) {
      this.primitive.setBands((this.data && this.data.bands) || [], this.enabled);
    }
  }

  _handleBlob(data) {
    if (!data || !Array.isArray(data.bands)) return;
    this.data = data;
    dlog(`[HVN ZONES] blob: ${data.bands.length} bands from ${
      (data.session_dates || []).length} sessions`);
    this._redraw();
  }

  _connectSSE() {
    this._disconnectSSE();
    this._sseUnsubs.push(
      unifiedSSE.subscribe('hvn-zones', 'hvn_zones_snapshot', (d) => this._handleBlob(d))
    );
    this._sseUnsubs.push(
      unifiedSSE.subscribe('hvn-zones', 'hvn_zones_update', (d) => this._handleBlob(d))
    );
  }

  _disconnectSSE() {
    this._sseUnsubs.forEach(unsub => unsub());
    this._sseUnsubs = [];
  }

  async _fetchInitial() {
    try {
      const response = await authFetch(`${CONFIG.apiUrl}/api/indicators/hvn-zones?symbol=ES`);
      if (response.ok) {
        this._handleBlob(await response.json());
      }
    } catch (error) {
      console.error('[HVN ZONES] initial fetch failed:', error);
    }
  }

  async enable() {
    this.enabled = true;
    if (!this.primitive) {
      this.primitive = new HVNZonesPrimitive(this.series);
      this.series.attachPrimitive(this.primitive);
    }
    this._connectSSE();
    if (!this.data) {
      await this._fetchInitial();
    }
    this._redraw();
  }

  disable() {
    this.enabled = false;
    this._disconnectSSE();
    if (this.primitive) {
      this.series.detachPrimitive(this.primitive);
      this.primitive = null;
    }
  }

  isEnabled() {
    return this.enabled;
  }
}

export default HVNZonesPlugin;
